import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Textarea, Price } from '@artosera/ui';
import {
  useApprobations,
  useDeciderApprobation,
  type ApprobationNegociation,
} from '../../api/presentation.js';
import { useOeuvre } from '../../api/oeuvres.js';
import { useContact } from '../../api/contacts.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { ErreurPresentation } from './ErreurPresentation.js';
import styles from './Presentation.module.css';

export function DemandesApprobation() {
  const { t } = useTranslation('presentation');
  const { galerieSlug, galerie, compte } = useShellContext();
  const [page, setPage] = useState(1);
  const demandes = useApprobations(galerieSlug, { page });
  const peutApprouver = galerieALaction(galerie, 'negociation:approuver');
  return (
    <div className={styles.page}>
      {!peutApprouver ? <p>{t('approbations.propres')}</p> : null}
      {demandes.isPending ? <p role="status">{t('chargement')}</p> : null}
      <ErreurPresentation erreur={demandes.error} recharger={() => void demandes.refetch()} />
      {demandes.data?.approbations.length === 0 ? <p>{t('aucuneDemande')}</p> : null}
      <ul className={styles.liste}>
        {demandes.data?.approbations.map((demande) => (
          <CarteDemande
            key={demande.id}
            demande={demande}
            peutApprouver={peutApprouver}
            compteId={compte.id}
          />
        ))}
      </ul>
      {demandes.data && demandes.data.pagination.pages > 1 ? (
        <div className={styles.actions}>
          <Button disabled={page <= 1 || demandes.isFetching} onClick={() => setPage(page - 1)}>
            {t('precedent')}
          </Button>
          <span>{t('page', { page, pages: demandes.data.pagination.pages })}</span>
          <Button
            disabled={page >= demandes.data.pagination.pages || demandes.isFetching}
            onClick={() => setPage(page + 1)}
          >
            {t('suivant')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
function CarteDemande({
  demande,
  peutApprouver,
  compteId,
}: {
  demande: ApprobationNegociation;
  peutApprouver: boolean;
  compteId: string;
}) {
  const { t, i18n } = useTranslation('presentation');
  const { galerieSlug, galerie } = useShellContext();
  const peutLireOeuvre = galerieALaction(galerie, 'artworks:read');
  const peutLireContact = galerieALaction(galerie, 'contacts:read');
  const oeuvre = useOeuvre(galerieSlug, peutLireOeuvre ? demande.oeuvreId : null);
  const contact = useContact(galerieSlug, peutLireContact ? demande.contactId : null);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  return (
    <li className={styles.carte}>
      <div className={styles.actions}>
        {peutLireOeuvre ? (
          <Link className={styles.lien} to={`/${galerieSlug}/oeuvres/${demande.oeuvreId}`}>
            {oeuvre.data?.oeuvre.titre ?? demande.oeuvreId}
          </Link>
        ) : (
          <span>{demande.oeuvreId}</span>
        )}
        {peutLireContact ? (
          <Link className={styles.lien} to={`/${galerieSlug}/contacts/${demande.contactId}`}>
            {contact.data?.contact.nomAffiche ?? demande.contactId}
          </Link>
        ) : null}
        <span>{t(`approbations.${demande.statut}`)}</span>
      </div>
      <p>{t('approbations.demandeur', { email: demande.demandeur.email })}</p>
      <p>
        {t('approbations.prix')}{' '}
        {oeuvre.data ? (
          <Price
            cents={demande.prixProposeCentimes}
            currency={oeuvre.data.oeuvre.devise}
            lang={langue}
          />
        ) : (
          new Intl.NumberFormat(langue, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(demande.prixProposeCentimes / 100)
        )}
      </p>
      {demande.motifDemande ? <p>{demande.motifDemande}</p> : null}
      {demande.decision?.note ? <p>{demande.decision.note}</p> : null}
      {peutApprouver &&
      demande.statut === 'en_attente' &&
      demande.demandeur.compteId !== compteId ? (
        <DecisionApprobation demande={demande} />
      ) : null}
      {peutApprouver &&
      demande.statut === 'en_attente' &&
      demande.demandeur.compteId === compteId ? (
        <p>{t('approbations.autoApprobation')}</p>
      ) : null}
    </li>
  );
}
function DecisionApprobation({ demande }: { demande: ApprobationNegociation }) {
  const { t } = useTranslation('presentation');
  const { galerieSlug } = useShellContext();
  const decider = useDeciderApprobation(galerieSlug);
  const [note, setNote] = useState('');
  const [refusSansNote, setRefusSansNote] = useState(false);
  return (
    <div className={styles.formulaire}>
      <label className={styles.champ}>
        {t('approbations.note')}
        <Textarea
          maxLength={1000}
          value={note}
          aria-invalid={refusSansNote}
          aria-describedby={refusSansNote ? `refus-${demande.id}` : undefined}
          onChange={(event) => {
            setNote(event.target.value);
            setRefusSansNote(false);
          }}
        />
      </label>
      {refusSansNote ? (
        <p role="alert" id={`refus-${demande.id}`}>
          {t('approbations.noteRequise')}
        </p>
      ) : null}
      <ErreurPresentation erreur={decider.error} />
      <div className={styles.actions}>
        <Button
          loading={decider.isPending}
          onClick={() =>
            decider.mutate({ id: demande.id, decision: 'acceptee', note: note.trim() })
          }
        >
          {t('approbations.accepter')}
        </Button>
        <Button
          variant="danger"
          disabled={decider.isPending}
          onClick={() => {
            if (!note.trim()) {
              setRefusSansNote(true);
              return;
            }
            decider.mutate({ id: demande.id, decision: 'refusee', note: note.trim() });
          }}
        >
          {t('approbations.refuser')}
        </Button>
      </div>
    </div>
  );
}
